// src/store/modules/callGraphProfiles.js
import CallGraphProfileService from '@/services/callGraphProfileService'

const V1_GRAPH_TYPES = ['USER', 'NUMBER']
function isSupportedGraphType(t) {
  return V1_GRAPH_TYPES.includes(String(t || '').trim())
}
function coerceGraphType(t) {
  const v = String(t || '').trim()
  return isSupportedGraphType(v) ? v : 'USER'
}

const state = () => ({
  customerId: null,
  profiles: [],
  selectedProfileId: null,
  loading: false,
  saving: false,
  error: null,

  // when create/rotate returns token/url, we store it here
  lastPublicLink: null
})

const getters = {
  profiles: (s) => s.profiles,
  loading: (s) => s.loading,
  saving: (s) => s.saving,
  error: (s) => s.error,
  selectedProfileId: (s) => s.selectedProfileId,
  lastPublicLink: (s) => s.lastPublicLink
}

/**
 * Normalize API -> UI shape expected by CallGraphs.vue
 * V1: only USER + NUMBER are supported.
 */
function normalizeProfile(apiRow) {
  const r = apiRow || {}

  const id = r.id ?? r.ID ?? null

  // hard-coerce graph type
  const graphTypeRaw = String(r.graph_type ?? r.graphType ?? '').trim()
  const graphType = coerceGraphType(graphTypeRaw || 'USER')

  // enabled_entities -> entityRefs
  const enabledEntities = Array.isArray(r.enabled_entities) ? r.enabled_entities : []
  const entityRefs = enabledEntities
    .filter((e) => (e?.is_enabled ?? true) === true)
    .filter((e) => coerceGraphType(e?.entity_type ?? e?.entityType ?? graphType) === graphType) // V1 guard
    .sort((a, b) => Number(a?.display_order ?? 0) - Number(b?.display_order ?? 0))
    .map((e) => String(e?.entity_ref ?? '').trim())
    .filter(Boolean)

  // config_json can be [] or {} depending on backend cast; normalize to object
  const cfgRaw = r.config_json
  const configJson =
    cfgRaw && typeof cfgRaw === 'object' && !Array.isArray(cfgRaw)
      ? cfgRaw
      : {}

  const metrics = Array.isArray(r.metrics)
    ? r.metrics
    : (Array.isArray(configJson.metrics) ? configJson.metrics : [])

  const chartType = String(r.chartType ?? configJson.chartType ?? 'bar').trim() || 'bar'
  const barLayout = String(r.barLayout ?? configJson.barLayout ?? 'BY_ENTITY').trim() || 'BY_ENTITY'

  const defaultTimeframe = String(r.default_timeframe ?? r.defaultTimeframe ?? 'LAST_7_DAYS').trim() || 'LAST_7_DAYS'
  const defaultInterval = String(r.default_interval ?? r.defaultInterval ?? '1_HOUR').trim() || '1_HOUR'

  // enabled/disabled indicator
  const hasPublicLink =
    !!String(r.hasPublicLink ?? '').trim()
      ? !!r.hasPublicLink
      : !!String(r.public_token_hash ?? '').trim()

  return {
    id,
    name: r.name ?? '',
    description: r.description ?? null,
    graphType,
    defaultTimeframe,
    defaultInterval,
    timezone: r.timezone ?? null,

    // UI expects these
    entityRefs,
    enabled_entities: enabledEntities,
    metrics,
    chartType,
    barLayout,

    configJson,
    hasPublicLink,

    public_token_hash: r.public_token_hash ?? null,

    // if your controller returns these, keep them around
    public_url: r.public_url ?? null,
    public_token: r.public_token ?? null
  }
}

function unwrapList(res) {
  const payload = res?.data ?? res
  if (!payload) return []
  if (Array.isArray(payload.data)) return payload.data
  if (Array.isArray(payload)) return payload
  return []
}

function firstSupportedId(list) {
  const p = (Array.isArray(list) ? list : []).find(x => x?.id && isSupportedGraphType(x?.graphType))
  return p?.id ?? null
}

const mutations = {
  SET_CUSTOMER_ID(s, v) { s.customerId = v ? String(v) : null },
  SET_LOADING(s, v) { s.loading = !!v },
  SET_SAVING(s, v) { s.saving = !!v },
  SET_ERROR(s, v) { s.error = v },

  SET_PROFILES(s, list) {
    const normalized = (Array.isArray(list) ? list : [])
      .map(normalizeProfile)
      .filter((p) => !!p.id)
      // V1: remove unsupported profiles
      .filter((p) => isSupportedGraphType(p.graphType))

    s.profiles = normalized

    // keep selection valid
    if (!s.selectedProfileId) {
      s.selectedProfileId = firstSupportedId(s.profiles)
      return
    }

    const stillExists = s.profiles.some(p => String(p.id) === String(s.selectedProfileId))
    if (!stillExists) s.selectedProfileId = firstSupportedId(s.profiles)
  },

  UPSERT_PROFILE(s, apiRow) {
    const p = normalizeProfile(apiRow)
    if (!p.id) return

    // V1: drop unsupported
    if (!isSupportedGraphType(p.graphType)) return

    const idx = s.profiles.findIndex((x) => String(x.id) === String(p.id))
    if (idx === -1) s.profiles = [p, ...s.profiles]
    else s.profiles.splice(idx, 1, p)

    // keep selection valid
    if (!s.selectedProfileId) s.selectedProfileId = p.id
  },

  PATCH_PROFILE(s, { id, patch } = {}) {
    if (!id || !patch) return
    const idx = s.profiles.findIndex((p) => String(p.id) === String(id))
    if (idx === -1) return

    const next = { ...s.profiles[idx], ...patch }

    // V1: if patch makes it unsupported, remove it
    if (!isSupportedGraphType(next.graphType)) {
      s.profiles.splice(idx, 1)
      if (String(s.selectedProfileId) === String(id)) {
        s.selectedProfileId = firstSupportedId(s.profiles)
      }
      return
    }

    s.profiles.splice(idx, 1, next)
  },

  REMOVE_PROFILE(s, id) {
    s.profiles = s.profiles.filter((p) => String(p.id) !== String(id))
    if (String(s.selectedProfileId) === String(id)) {
      s.selectedProfileId = firstSupportedId(s.profiles)
    }
  },

  SET_SELECTED(s, id) {
    // only allow selecting supported profiles
    const found = s.profiles.find(p => String(p.id) === String(id))
    s.selectedProfileId = found ? found.id : firstSupportedId(s.profiles)
  },

  SET_LAST_PUBLIC_LINK(s, v) {
    s.lastPublicLink = v || null
  },

  CLEAR_LAST_PUBLIC_LINK(s) {
    s.lastPublicLink = null
  }
}

const actions = {
  setCustomerId({ commit }, cid) {
    commit('SET_CUSTOMER_ID', cid)
  },

  selectProfile({ commit }, id) {
    commit('SET_SELECTED', id)
  },

  async fetchProfiles({ state, commit }, params = {}) {
    if (!state.customerId) throw new Error('callGraphProfiles: missing customerId')
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const res = await CallGraphProfileService.list({
        customer_id: state.customerId,
        q: params.q ?? '',
        // V1: only pass graph_type if supported
        graph_type: params.graph_type && isSupportedGraphType(params.graph_type) ? params.graph_type : undefined,
        include_entities: params.include_entities ?? 1
      })
      commit('SET_PROFILES', unwrapList(res))
      return state.profiles
    } catch (e) {
      commit('SET_ERROR', e)
      throw e
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async getProfile({ state, commit }, id) {
    if (!state.customerId) throw new Error('callGraphProfiles: missing customerId')
    if (!id) throw new Error('callGraphProfiles: missing id')
    commit('SET_ERROR', null)
    const res = await CallGraphProfileService.get(id, {
      customer_id: state.customerId,
      include_entities: 1
    })
    const row = (res?.data?.data ?? res?.data ?? res)
    commit('UPSERT_PROFILE', row)
    return normalizeProfile(row)
  },

  async createProfile({ state, commit, dispatch }, payload) {
    if (!state.customerId) throw new Error('callGraphProfiles: missing customerId')
    commit('SET_SAVING', true)
    commit('SET_ERROR', null)
    try {
      // V1 guard
      const graphType = coerceGraphType(payload?.graphType ?? payload?.graph_type ?? 'USER')

      const res = await CallGraphProfileService.create({
        customer_id: state.customerId,
        ...payload,
        graphType
      })

      const row = res?.data?.data ?? res?.data ?? res
      commit('UPSERT_PROFILE', row)

      const createdId = row?.id ?? row?.data?.id
      if (createdId) {
        commit('SET_SELECTED', createdId)
        await dispatch('getProfile', createdId)
      }
      return normalizeProfile(row)
    } catch (e) {
      commit('SET_ERROR', e)
      throw e
    } finally {
      commit('SET_SAVING', false)
    }
  },

  async updateProfile({ state, commit, dispatch }, { profileId, draft } = {}) {
    if (!state.customerId) throw new Error('callGraphProfiles: missing customerId')
    if (!profileId) throw new Error('callGraphProfiles: missing profileId')
    commit('SET_SAVING', true)
    commit('SET_ERROR', null)
    try {
      // V1 guard
      const graphType = coerceGraphType(draft?.graphType ?? draft?.graph_type)

      const res = await CallGraphProfileService.update(profileId, {
        customer_id: state.customerId,
        ...draft,
        graphType
      })
      const row = res?.data?.data ?? res?.data ?? res
      commit('UPSERT_PROFILE', row)
      await dispatch('getProfile', profileId)
      return normalizeProfile(row)
    } catch (e) {
      commit('SET_ERROR', e)
      throw e
    } finally {
      commit('SET_SAVING', false)
    }
  },

  async deleteProfile({ state, commit }, id) {
    if (!state.customerId) throw new Error('callGraphProfiles: missing customerId')
    if (!id) throw new Error('callGraphProfiles: missing id')
    commit('SET_SAVING', true)
    commit('SET_ERROR', null)
    try {
      await CallGraphProfileService.remove(id, { customer_id: state.customerId })
      commit('REMOVE_PROFILE', id)
      return true
    } catch (e) {
      commit('SET_ERROR', e)
      throw e
    } finally {
      commit('SET_SAVING', false)
    }
  },

  // =========================
  // Public link actions
  // =========================
  async createOrRotatePublicLink({ state, commit }, id) {
    if (!state.customerId) throw new Error('callGraphProfiles: missing customerId')
    if (!id) throw new Error('callGraphProfiles: missing id')
    commit('SET_SAVING', true)
    commit('SET_ERROR', null)
    try {
      const res = await CallGraphProfileService.createOrRotatePublicLink(id, { customer_id: state.customerId })
      const data = res?.data?.data ?? res?.data ?? res

      const publicUrl = data?.public_url ?? data?.url ?? null
      const token = data?.token ?? null

      const stored = {
        profile_id: data?.profile_id ?? id,
        url: publicUrl,
        public_url: publicUrl,
        token
      }

      commit('SET_LAST_PUBLIC_LINK', stored)
      commit('PATCH_PROFILE', { id, patch: { hasPublicLink: true, public_url: publicUrl } })

      return stored
    } catch (e) {
      commit('SET_ERROR', e)
      throw e
    } finally {
      commit('SET_SAVING', false)
    }
  },

  async disablePublicLink({ state, commit }, id) {
    if (!state.customerId) throw new Error('callGraphProfiles: missing customerId')
    if (!id) throw new Error('callGraphProfiles: missing id')
    commit('SET_SAVING', true)
    commit('SET_ERROR', null)
    try {
      await CallGraphProfileService.disablePublicLink(id, { customer_id: state.customerId })
      commit('CLEAR_LAST_PUBLIC_LINK')
      commit('PATCH_PROFILE', { id, patch: { hasPublicLink: false, public_url: null } })
      return true
    } catch (e) {
      commit('SET_ERROR', e)
      throw e
    } finally {
      commit('SET_SAVING', false)
    }
  },

  clearLastPublicLink({ commit }) {
    commit('CLEAR_LAST_PUBLIC_LINK')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
